import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Popover,
  Box,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import TranslateIcon from "@material-ui/icons/Translate";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";

import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import TicketsWhatsappSelect from "../components/TicketsWhatsappSelect";
import { AuthContext } from "../context/Auth/AuthContext";
import TranslationContext from "../context/Translation/TranslationContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { useThemeContext } from "../context/DarkMode";
import toastError from "../errors/toastError";
import api from "../services/api";
import openSocket from "../services/socket-io";
import { TRANSLATION_LANGUAGES } from "../constants/translationLanguages";
import { toast } from "react-toastify";
import useWhatsApps from "../hooks/useWhatsApps";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    minHeight: "48px",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    backgroundColor: theme.palette.background.default,
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
    color: theme.palette.text.primary,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    backgroundColor: theme.palette.background.paper,
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: {
    minHeight: "48px",
  },
  content: {
    flex: 1,
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  switch: {
    transform: "scale(0.8)",
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  themeSwitchContainer: {
    display: "flex",
    alignItems: "center",
  },
  themeIcon: {
    color: theme.palette.text.primary,
  },
  translationMenuPaper: {
    minWidth: 280,
    maxHeight: "80vh",
  },
  translationMenuSection: {
    padding: "8px 16px",
    width: 280,
    boxSizing: "border-box",
  },
  translationKeyStatus: {
    display: "block",
    fontSize: "0.8rem",
    marginBottom: theme.spacing(1),
    color: theme.palette.success.main,
  },
  translationKeyStatusMissing: {
    color: theme.palette.warning.main,
  },
  translationPrivacyNote: {
    display: "block",
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
    marginBottom: theme.spacing(1),
  },
}));

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, handleToggleAutoAttend } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
  const { darkMode, toggleTheme } = useThemeContext();
  const { whatsApps } = useWhatsApps();
  const [autoAttendLoading, setAutoAttendLoading] = useState(false);
  const [autoAttendAnchor, setAutoAttendAnchor] = useState(null);
  const [selectedAutoAttendWhatsappIds, setSelectedAutoAttendWhatsappIds] = useState([]);
  const [disableAllAiLoading, setDisableAllAiLoading] = useState(false);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [translationConfigured, setTranslationConfigured] = useState(false);
  const [translationLoading, setTranslationLoading] = useState(false);
  const [agentLanguage, setAgentLanguage] = useState("pt");
  const [customerLanguage, setCustomerLanguage] = useState("fr");
  const [translationGoogleApiKey, setTranslationGoogleApiKey] = useState("");
  const [translationLibreUrl, setTranslationLibreUrl] = useState(
    "http://127.0.0.1:5000"
  );
  const [hasLibreProvider, setHasLibreProvider] = useState(false);
  const [hasGoogleStandby, setHasGoogleStandby] = useState(false);
  const [savingTranslationApiKey, setSavingTranslationApiKey] = useState(false);
  const [savingLibreUrl, setSavingLibreUrl] = useState(false);
  const [translationMenuAnchor, setTranslationMenuAnchor] = useState(null);

  const applyTranslationSettings = (data) => {
    setTranslationConfigured(Boolean(data.configured));
    setTranslationEnabled(Boolean(data.enabled));
    setAgentLanguage(data.agentLanguage || "pt");
    setCustomerLanguage(data.customerLanguage || "fr");
    if (data.libreUrl !== undefined) {
      setTranslationLibreUrl(data.libreUrl || "");
    }
    setHasLibreProvider(Boolean(data.hasLibre));
    setHasGoogleStandby(Boolean(data.hasGoogleStandby));
  };

  useEffect(() => {
    const fetchTranslationSettings = async () => {
      try {
        const { data } = await api.get("/translate/settings");
        applyTranslationSettings(data);
      } catch (err) {
        setTranslationConfigured(false);
        setTranslationEnabled(false);
      }
    };

    fetchTranslationSettings();
  }, []);

  useEffect(() => {
    const socket = openSocket();
    if (!socket) return undefined;

    const handleSettings = data => {
      if (
        data.action === "update" &&
        data.setting?.key === "translationEnabled"
      ) {
        setTranslationEnabled(data.setting.value === "true");
      }
      if (
        data.action === "update" &&
        data.setting?.key === "translationCustomerLanguage"
      ) {
        setCustomerLanguage(data.setting.value || "fr");
      }
      if (
        data.action === "update" &&
        data.setting?.key === "translationAgentLanguage"
      ) {
        setAgentLanguage(data.setting.value || "pt");
      }
      if (
        data.action === "update" &&
        data.setting?.key === "translationGoogleApiKey"
      ) {
        setHasGoogleStandby(Boolean(data.setting.value?.trim()));
      }
      if (
        data.action === "update" &&
        data.setting?.key === "translationLibreUrl"
      ) {
        setTranslationLibreUrl(data.setting.value || "");
        setHasLibreProvider(Boolean(data.setting.value?.trim()));
      }
    };

    socket.on("settings", handleSettings);

    return () => {
      socket.off("settings", handleSettings);
    };
  }, []);

  useEffect(() => {
    setTranslationConfigured(hasLibreProvider || hasGoogleStandby);
  }, [hasLibreProvider, hasGoogleStandby]);

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const handleAutoAttendToggle = async (event) => {
    if (!handleToggleAutoAttend) return;

    if (user?.autoAttendEnabled) {
      setAutoAttendLoading(true);
      try {
        await handleToggleAutoAttend(false);
      } catch (err) {
        toastError(err);
      } finally {
        setAutoAttendLoading(false);
      }
      return;
    }

    const preset =
      Array.isArray(user?.autoAttendWhatsappIds) && user.autoAttendWhatsappIds.length
        ? user.autoAttendWhatsappIds
        : (whatsApps || []).map(wa => wa.id);
    setSelectedAutoAttendWhatsappIds(preset);
    setAutoAttendAnchor(event.currentTarget);
  };

  const handleOpenAutoAttendSelector = (event) => {
    const preset =
      Array.isArray(user?.autoAttendWhatsappIds) && user.autoAttendWhatsappIds.length
        ? user.autoAttendWhatsappIds
        : (whatsApps || []).map(wa => wa.id);
    setSelectedAutoAttendWhatsappIds(preset);
    setAutoAttendAnchor(event.currentTarget);
  };

  const handleCloseAutoAttendSelector = () => {
    setAutoAttendAnchor(null);
  };

  const handleConfirmAutoAttend = async () => {
    if (!handleToggleAutoAttend) return;
    if (!selectedAutoAttendWhatsappIds.length) {
      toast.error(i18n.t("mainDrawer.appBar.autoAttendSelectNumbers"));
      return;
    }
    setAutoAttendLoading(true);
    try {
      await handleToggleAutoAttend(true, selectedAutoAttendWhatsappIds);
      setAutoAttendAnchor(null);
    } catch (err) {
      toastError(err);
    } finally {
      setAutoAttendLoading(false);
    }
  };

  const handleDisableAllAi = async () => {
    const ok = window.confirm(i18n.t("mainDrawer.appBar.disableAllAiConfirm"));
    if (!ok) return;
    setDisableAllAiLoading(true);
    try {
      const { data } = await api.post("/ai/tickets/disable-all");
      toast.success(
        i18n.t("mainDrawer.appBar.disableAllAiDone", {
          count: data?.paused ?? 0,
        })
      );
    } catch (err) {
      toastError(err);
    } finally {
      setDisableAllAiLoading(false);
    }
  };

  const handleTranslationToggle = async () => {
    const nextValue = !translationEnabled;
    setTranslationLoading(true);
    try {
      const { data } = await api.put("/translate/settings", {
        enabled: nextValue,
      });
      applyTranslationSettings(data);
    } catch (err) {
      toastError(err);
    } finally {
      setTranslationLoading(false);
    }
  };

  const handleCustomerLanguageSelect = async (languageCode) => {
    try {
      const { data } = await api.put("/translate/settings", {
        customerLanguage: languageCode,
      });
      applyTranslationSettings(data);
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleAgentLanguageSelect = async (languageCode) => {
    try {
      const { data } = await api.put("/translate/settings", {
        agentLanguage: languageCode,
      });
      applyTranslationSettings(data);
      toast.success(i18n.t("settings.success"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveLibreUrl = async () => {
    setSavingLibreUrl(true);
    try {
      const { data } = await api.put("/translate/settings", {
        libreUrl: translationLibreUrl.trim(),
      });
      applyTranslationSettings(data);
      toast.success(i18n.t("mainDrawer.appBar.translationLibreSaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingLibreUrl(false);
    }
  };

  const handleSaveTranslationApiKey = async () => {
    if (!translationGoogleApiKey.trim()) {
      toast.error(i18n.t("mainDrawer.appBar.translationApiKeyEmpty"));
      return;
    }

    setSavingTranslationApiKey(true);
    try {
      const { data } = await api.put("/translate/settings", {
        apiKey: translationGoogleApiKey.trim(),
      });
      applyTranslationSettings(data);
      setTranslationGoogleApiKey("");
      toast.success(i18n.t("mainDrawer.appBar.translationKeySaved"));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingTranslationApiKey(false);
    }
  };

  const translationContextValue = {
    translationEnabled,
    translationConfigured,
    translationLoading,
    agentLanguage,
    customerLanguage,
    toggleTranslation: handleTranslationToggle,
    setAgentLanguage: handleAgentLanguageSelect,
    setCustomerLanguage: handleCustomerLanguageSelect,
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <TranslationContext.Provider value={translationContextValue}>
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List>
          <MainListItems drawerClose={drawerClose} />
        </List>
        <Divider />
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            noWrap
            className={classes.title}
          >
            WhaTicket
          </Typography>

          <div className={classes.themeSwitchContainer}>
            <Brightness4Icon className={classes.themeIcon} />
            <Switch
              checked={darkMode}
              onChange={toggleTheme}
              color="default"
              className={classes.switch}
            />
          </div>

          {user.id && (
            <>
              <IconButton
                className={classes.iconButton}
                onClick={(e) => setTranslationMenuAnchor(e.currentTarget)}
                title={
                  translationConfigured
                    ? i18n.t("mainDrawer.appBar.translation")
                    : i18n.t("mainDrawer.appBar.translationNeedsKey")
                }
              >
                <TranslateIcon />
              </IconButton>
              <Menu
                anchorEl={translationMenuAnchor}
                open={Boolean(translationMenuAnchor)}
                onClose={() => setTranslationMenuAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ className: classes.translationMenuPaper }}
              >
                <div
                  className={classes.translationMenuSection}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Typography
                    variant="caption"
                    className={classes.translationPrivacyNote}
                  >
                    {i18n.t("mainDrawer.appBar.translationPrivacyNote")}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={translationEnabled}
                        onChange={handleTranslationToggle}
                        color="primary"
                        className={classes.switch}
                        disabled={translationLoading || !translationConfigured}
                      />
                    }
                    label={i18n.t("mainDrawer.appBar.translationEnabled")}
                  />
                </div>
                <Divider />
                <div
                  className={classes.translationMenuSection}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Typography
                    variant="caption"
                    className={clsx(classes.translationKeyStatus, {
                      [classes.translationKeyStatusMissing]: !hasLibreProvider,
                    })}
                  >
                    {hasLibreProvider
                      ? i18n.t("mainDrawer.appBar.translationLibreConfigured")
                      : i18n.t("mainDrawer.appBar.translationLibreMissing")}
                  </Typography>
                  <TextField
                    label={i18n.t("mainDrawer.appBar.translationLibreUrl")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={translationLibreUrl}
                    onChange={(e) => setTranslationLibreUrl(e.target.value)}
                    helperText={i18n.t("mainDrawer.appBar.translationLibreHelp")}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    fullWidth
                    style={{ marginTop: 8 }}
                    onClick={handleSaveLibreUrl}
                    disabled={savingLibreUrl}
                  >
                    {i18n.t("mainDrawer.appBar.translationSaveLibre")}
                  </Button>
                </div>
                <Divider />
                <div
                  className={classes.translationMenuSection}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Typography
                    variant="caption"
                    className={clsx(classes.translationKeyStatus, {
                      [classes.translationKeyStatusMissing]: !hasGoogleStandby,
                    })}
                  >
                    {hasGoogleStandby
                      ? i18n.t("mainDrawer.appBar.translationKeyConfigured")
                      : i18n.t("mainDrawer.appBar.translationKeyMissing")}
                  </Typography>
                  <TextField
                    label={i18n.t("mainDrawer.appBar.translationApiKey")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    type="password"
                    value={translationGoogleApiKey}
                    placeholder={
                      hasGoogleStandby
                        ? i18n.t("mainDrawer.appBar.translationApiKeyReplace")
                        : ""
                    }
                    onChange={(e) => setTranslationGoogleApiKey(e.target.value)}
                    helperText={i18n.t("mainDrawer.appBar.translationApiKeyHelp")}
                  />
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    fullWidth
                    style={{ marginTop: 8 }}
                    onClick={handleSaveTranslationApiKey}
                    disabled={savingTranslationApiKey}
                  >
                    {i18n.t("mainDrawer.appBar.translationSaveApiKey")}
                  </Button>
                </div>
                <Divider />
                <MenuItem disabled dense>
                  <Typography variant="caption">
                    {i18n.t("mainDrawer.appBar.agentLanguage")}
                  </Typography>
                </MenuItem>
                {TRANSLATION_LANGUAGES.map((lang) => (
                  <MenuItem
                    key={`agent-${lang.code}`}
                    selected={agentLanguage === lang.code}
                    onClick={() => handleAgentLanguageSelect(lang.code)}
                  >
                    {lang.label}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem disabled dense>
                  <Typography variant="caption">
                    {i18n.t("mainDrawer.appBar.outputLanguage")}
                  </Typography>
                </MenuItem>
                {TRANSLATION_LANGUAGES.map((lang) => (
                  <MenuItem
                    key={`output-${lang.code}`}
                    selected={customerLanguage === lang.code}
                    onClick={() => handleCustomerLanguageSelect(lang.code)}
                  >
                    {lang.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {user.id && (
            <div className={classes.themeSwitchContainer} title={i18n.t("mainDrawer.appBar.autoAttend")}>
              <HeadsetMicIcon
                className={classes.themeIcon}
                style={{ cursor: "pointer" }}
                onClick={handleOpenAutoAttendSelector}
              />
              <Switch
                checked={Boolean(user.autoAttendEnabled)}
                onChange={handleAutoAttendToggle}
                color="secondary"
                className={classes.switch}
                disabled={autoAttendLoading}
              />
              <IconButton
                size="small"
                className={classes.iconButton}
                title={i18n.t("mainDrawer.appBar.disableAllAi")}
                onClick={handleDisableAllAi}
                disabled={disableAllAiLoading}
              >
                <PowerSettingsNewIcon fontSize="small" />
              </IconButton>
              <Popover
                open={Boolean(autoAttendAnchor)}
                anchorEl={autoAttendAnchor}
                onClose={handleCloseAutoAttendSelector}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <Box p={2} style={{ minWidth: 260 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {i18n.t("mainDrawer.appBar.autoAttendSelectNumbers")}
                  </Typography>
                  <TicketsWhatsappSelect
                    whatsApps={whatsApps}
                    selectedWhatsappIds={selectedAutoAttendWhatsappIds}
                    onChange={setSelectedAutoAttendWhatsappIds}
                    style={{ width: "100%", marginTop: 0 }}
                  />
                  <Box mt={1} display="flex" justifyContent="flex-end" gridGap={8}>
                    <Button size="small" onClick={handleCloseAutoAttendSelector}>
                      {i18n.t("mainDrawer.appBar.autoAttendCancel")}
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      disabled={autoAttendLoading || !selectedAutoAttendWhatsappIds.length}
                      onClick={handleConfirmAutoAttend}
                    >
                      {i18n.t("mainDrawer.appBar.autoAttendConfirm")}
                    </Button>
                  </Box>
                </Box>
              </Popover>
            </div>
          )}

          {user.id && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {children ? children : null}
      </main>
    </div>
    </TranslationContext.Provider>
  );
};

export default LoggedInLayout;
